import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import useGoogleDrive from '../../hooks/useGoogleDrive';
import PageHeader from '../../components/ui/PageHeader';

const GOOGLE_KEYS = ['google_client_id', 'google_client_secret', 'google_redirect_uri', 'google_drive_service_account_json', 'google_drive_backup_folder_id'];

const GOOGLE_FIELD_INFO = {
  google_client_id: { label: 'Google Client ID', type: 'text', help: <>El identificador público de tu aplicación OAuth. <strong>Cómo obtenerlo:</strong> Ve a <em>Google Cloud Console → APIs y servicios → Credenciales → Crear credenciales → ID de cliente de OAuth 2.0</em>. Copia el valor del campo <em>ID de cliente</em> (termina en <code>.apps.googleusercontent.com</code>).</> },
  google_client_secret: { label: 'Google Client Secret', type: 'password', help: <>La clave secreta de tu aplicación OAuth. <strong>Cómo obtenerlo:</strong> En la misma pantalla de credenciales de Google Cloud Console, copia el valor del campo <em>Secreto de cliente</em>.</> },
  google_redirect_uri: { label: 'Google Redirect URI', type: 'text', help: <>La URL de redirección después de la autenticación. Debe ser exactamente: <code>https://tu-dominio.com/api/auth/google/callback</code>. En local: <code>http://localhost:8080/api/auth/google/callback</code>.</> },
  google_drive_service_account_json: { label: 'Google Drive Service Account (JSON)', type: 'textarea', help: <>Contenido JSON de la cuenta de servicio para acceso a Google Drive. Ve a <em>IAM y administración → Cuentas de servicio</em>, crea una con rol <em>Editor de Drive</em>, genera una clave JSON y pega aquí el contenido.</> },
  google_drive_backup_folder_id: { label: 'Google Drive Backup Folder ID', type: 'text', help: <>ID de la carpeta de Google Drive para respaldos. Es la parte final de la URL de la carpeta: <code>drive.google.com/drive/folders/<strong>ESTE_ES_EL_ID</strong></code>.</> },
};

const inputSt   = { width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };
const monoInput = { ...inputSt, fontFamily: 'monospace', fontSize: 12 };
const focusSt   = e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
const blurSt    = e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; };

const SectionCard = ({ title, icon, children }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16, overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '13px 18px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 14, color: '#111827' }}>
      {icon}{title}
    </div>
    <div style={{ padding: 18 }}>{children}</div>
  </div>
);

const GoogleLogo = () => (
  <svg style={{ width: 18, height: 18, flexShrink: 0 }} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const SystemSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingGoogle, setSavingGoogle] = useState(false);
  const [form, setForm] = useState({});
  const [showSecret, setShowSecret] = useState({});
  const { connected, connect, disconnect } = useGoogleDrive();
  const [disconnecting, setDisconnecting] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  useEffect(() => {
    api.get('/settings/system').then(res => {
      setSettings(res.data);
      const init = {};
      res.data.forEach(s => { init[s.key] = s.value || ''; });
      setForm(init);
    }).finally(() => setLoading(false));
  }, []);

  const generalSettings = settings.filter(s => !GOOGLE_KEYS.includes(s.key));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put('/settings/system', { settings: generalSettings.map(s => ({ key: s.key, value: form[s.key] })) }); toast.success('Configuración guardada'); }
    catch { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  const handleSaveGoogle = async (e) => {
    e.preventDefault(); setSavingGoogle(true);
    try { await api.put('/settings/system', { settings: GOOGLE_KEYS.map(key => ({ key, value: form[key] || '' })) }); toast.success('Credenciales de Google guardadas'); }
    catch { toast.error('Error al guardar credenciales'); } finally { setSavingGoogle(false); }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try { await disconnect(); toast.success('Drive desconectado'); }
    catch { toast.error('Error al desconectar'); } finally { setDisconnecting(false); }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try { const res = await api.post('/backup'); toast.success(res.data.message); }
    catch { toast.error('Error al realizar el backup'); } finally { setBackingUp(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
      <div style={{ width: 28, height: 28, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );

  return (
    <div>
      <PageHeader title="Configuración del Sistema" subtitle="Parámetros generales e integraciones" />

      {/* Parámetros generales */}
      <SectionCard title="Parámetros generales">
        <form onSubmit={handleSubmit}>
          {generalSettings.map(s => (
            <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '220px 160px 1fr', gap: '0 20px', alignItems: 'start', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #F3F4F6' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{s.label}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2, fontFamily: 'monospace' }}>{s.key}</div>
              </div>
              <input type={s.type} value={form[s.key] || ''} onChange={e => setForm({ ...form, [s.key]: e.target.value })}
                min={s.type === 'number' ? 1 : undefined}
                style={inputSt} onFocus={focusSt} onBlur={blurSt} />
              <div style={{ fontSize: 12, color: '#9CA3AF', paddingTop: 8, lineHeight: 1.5 }}>
                {s.key === 'stale_case_file_days' && 'Los expedientes sin cambios por más de este número de días se marcarán con alerta en el dashboard.'}
              </div>
            </div>
          ))}
          <button type="submit" disabled={saving}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Guardando...' : 'Guardar configuración'}
          </button>
        </form>
      </SectionCard>

      {/* Credenciales Google */}
      <SectionCard title="Credenciales de Google API" icon={<GoogleLogo />}>
        <div style={{ padding: '10px 14px', borderRadius: 8, backgroundColor: '#EFF6FF', border: '1px solid #DBEAFE', fontSize: 13, color: '#1D4ED8', marginBottom: 20 }}>
          <strong>Antes de comenzar:</strong> Necesitas un proyecto en{' '}
          <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" style={{ color: '#1D4ED8', fontWeight: 600 }}>Google Cloud Console</a>{' '}
          con la <strong>Google Drive API</strong> habilitada. Ve a <em>APIs y servicios → Biblioteca</em>, busca "Google Drive API" y actívala.
        </div>

        <form onSubmit={handleSaveGoogle}>
          {GOOGLE_KEYS.map(key => {
            const info = GOOGLE_FIELD_INFO[key];
            const isPassword = info.type === 'password';
            const isTextarea = info.type === 'textarea';
            return (
              <div key={key} style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 3 }}>{info.label}</div>
                <div style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'monospace', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{key}</div>

                {isTextarea ? (
                  <textarea rows={6} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
                    placeholder='{ "type": "service_account", ... }' style={{ ...monoInput, resize: 'vertical' }} onFocus={focusSt} onBlur={blurSt} />
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type={isPassword && !showSecret[key] ? 'password' : 'text'} value={form[key] || ''}
                      onChange={e => setForm({ ...form, [key]: e.target.value })} style={{ ...inputSt, flex: 1 }} onFocus={focusSt} onBlur={blurSt} />
                    {isPassword && (
                      <button type="button" onClick={() => setShowSecret(p => ({ ...p, [key]: !p[key] }))}
                        style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {showSecret[key] ? 'Ocultar' : 'Mostrar'}
                      </button>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 7, backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
                  ℹ️ {info.help}
                </div>
              </div>
            );
          })}
          <button type="submit" disabled={savingGoogle}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: savingGoogle ? 0.7 : 1 }}>
            {savingGoogle ? 'Guardando...' : 'Guardar credenciales de Google'}
          </button>
        </form>
      </SectionCard>

      {/* Integraciones */}
      <SectionCard title="Integraciones">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GoogleLogo />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Google Drive</div>
              <div style={{ fontSize: 12, color: '#9CA3AF' }}>Almacenamiento de documentos y comprobantes de pago</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {connected ? (
              <>
                <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20, backgroundColor: '#D1FAE5', color: '#065F46' }}>✓ Conectado</span>
                <button onClick={connect} style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>Reconectar</button>
                <button onClick={handleDisconnect} disabled={disconnecting} style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #FECACA', backgroundColor: '#FEF2F2', fontSize: 12, fontWeight: 600, color: '#991B1B', cursor: 'pointer', opacity: disconnecting ? 0.7 : 1 }}>
                  {disconnecting ? 'Desconectando...' : 'Desconectar'}
                </button>
              </>
            ) : (
              <>
                <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20, backgroundColor: '#F3F4F6', color: '#374151' }}>Sin conexión</span>
                <button onClick={connect} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Conectar Drive</button>
              </>
            )}
          </div>
        </div>
        {connected && (
          <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, backgroundColor: '#EFF6FF', border: '1px solid #DBEAFE', fontSize: 12, color: '#1D4ED8' }}>
            <strong>Reconectar</strong> — úsalo si los archivos dejaron de subirse a Drive o si ves errores de token expirado.
          </div>
        )}
      </SectionCard>

      {/* Respaldo */}
      <SectionCard title="Respaldo de base de datos">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 3 }}>Backup a Google Drive</div>
            <div style={{ fontSize: 12, color: '#9CA3AF' }}>Genera un dump de la base de datos y lo sube a Google Drive. Asegúrate de tener Drive conectado.</div>
          </div>
          <button onClick={handleBackup} disabled={backingUp || !connected}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1.5px solid #4F63D2', backgroundColor: '#EEF0FB', color: '#4F63D2', fontWeight: 600, fontSize: 13, cursor: backingUp || !connected ? 'not-allowed' : 'pointer', opacity: !connected ? 0.5 : 1 }}>
            {backingUp ? (
              <><div style={{ width: 12, height: 12, border: '2px solid #4F63D2', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />Respaldando...</>
            ) : (
              <><svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>Respaldar ahora</>
            )}
          </button>
        </div>
        {!connected && (
          <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', fontSize: 12, color: '#92400E' }}>
            Conecta Google Drive primero para poder realizar el respaldo.
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default SystemSettings;
